import React, { useState } from 'react'
import { Link, Head } from '@inertiajs/react'
import {
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  LifeBuoy,
  Loader2,
  CreditCard,
  LogIn,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useBrand } from '@/hooks/useBrand'
import { SUPPORT_EMAIL } from '@/lib/brand'
import { confirmParkedPaymentIntent } from '@/lib/stripe-client'

// ReAuth (B2) — the page the SCA re-authentication email links to.
//
// A BARE page: it mounts no shared layout (the visitor is very likely signed out and has no
// business context), so it renders its own <Head title>.
//
// It CONFIRMS a payment; it decides nothing about billing. The backend read the parked
// PaymentIntent server-side and handed down a client_secret plus finished display strings — this
// page formats no money, computes no date and re-derives no state (Data Integrity: display only).
// Convergence to paid is the existing payment_intent.succeeded webhook, never this page.

// Server-classified: 'ready' — a parked PaymentIntent is waiting for the challenge; 'settled' —
// nothing left to confirm; 'unavailable' — the token was forged, expired, or names a payment that
// is gone (deliberately one outcome, so a rejected link confirms nothing).
type ReAuthState = 'ready' | 'settled' | 'unavailable'

interface ReAuthProps {
  state: ReAuthState
  // The parked PaymentIntent's client_secret, fetched server-side through the Stripe seam so the
  // page can only ever confirm the intent the emailed token names. null unless state is 'ready'.
  clientSecret: string | null
  stripePublishableKey: string | null
  // Server-formatted (`number_to_currency`) — never a number this page rounds or re-formats.
  amount: string | null
  invoiceNumber: string | null
  // Server-formatted re-auth deadline string.
  deadline: string | null
  // Ready-made path (backend-served, never assembled here). null on a rejected token.
  billingPath: string | null
  signInPath: string
  // Why there is nothing to confirm, composed on the backend.
  message: string | null
}

type ConfirmPhase = 'idle' | 'confirming' | 'confirmed' | 'failed'

export default function ReAuth({
  state,
  clientSecret,
  stripePublishableKey,
  amount,
  invoiceNumber,
  deadline,
  billingPath,
  signInPath,
  message,
}: ReAuthProps) {
  const brand = useBrand()
  const [phase, setPhase] = useState<ConfirmPhase>('idle')
  const [error, setError] = useState<string | null>(null)

  async function handleConfirm() {
    if (!clientSecret || !stripePublishableKey || phase === 'confirming') return
    setPhase('confirming')
    setError(null)
    try {
      const outcome = await confirmParkedPaymentIntent(stripePublishableKey, clientSecret)
      if (outcome.status === 'succeeded') {
        setPhase('confirmed')
        return
      }
      setPhase('failed')
      setError(
        outcome.error?.message ??
          'The check wasn’t completed. You can try again, or retry the payment from your Billing page.'
      )
    } catch {
      setPhase('failed')
      setError('Something went wrong reaching your bank. Please try again in a moment.')
    }
  }

  const confirmable = state === 'ready' && Boolean(clientSecret) && Boolean(stripePublishableKey)

  return (
    <div className="min-h-screen bg-background px-4 py-12">
      <Head title="Confirm your payment" />
      <div className="mx-auto max-w-lg">
        {state === 'settled' || phase === 'confirmed' ? (
          <ConfirmedCard
            confirmedNow={phase === 'confirmed'}
            billingPath={billingPath}
            signInPath={signInPath}
          />
        ) : state === 'unavailable' || !confirmable ? (
          <UnavailableCard message={message} signInPath={signInPath} />
        ) : (
          <ConfirmCard
            brand={brand}
            amount={amount}
            invoiceNumber={invoiceNumber}
            deadline={deadline}
            phase={phase}
            error={error}
            onConfirm={handleConfirm}
          />
        )}
      </div>
    </div>
  )
}

// The live challenge. One button: re-confirming the parked PaymentIntent on-session is what makes
// the bank show its check. No card form — the payment method is already attached to this intent.
function ConfirmCard({
  brand,
  amount,
  invoiceNumber,
  deadline,
  phase,
  error,
  onConfirm,
}: {
  brand: string
  amount: string | null
  invoiceNumber: string | null
  deadline: string | null
  phase: ConfirmPhase
  error: string | null
  onConfirm: () => void
}) {
  return (
    <div className="animate-slide-in overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
      <div className="bg-gradient-to-b from-primary/5 to-white px-8 pt-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <ShieldCheck className="h-8 w-8 text-primary" />
        </div>
        <h1 className="mt-5 text-xl font-bold tracking-tight text-foreground">
          Confirm your payment
        </h1>
        <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
          Your bank needs a one-time security check before this payment can go through. Confirming
          takes a few seconds and keeps your access running.
        </p>
      </div>

      <div className="space-y-4 p-8 pt-6">
        <dl className="space-y-2 rounded-lg border border-border/60 bg-muted/40 px-4 py-3 text-sm">
          {amount && (
            <div className="flex items-center justify-between gap-4">
              <dt className="text-muted-foreground">Amount</dt>
              <dd className="font-semibold tabular-nums text-foreground">{amount}</dd>
            </div>
          )}
          {invoiceNumber && (
            <div className="flex items-center justify-between gap-4">
              <dt className="text-muted-foreground">Invoice</dt>
              <dd className="tabular-nums text-foreground">{invoiceNumber}</dd>
            </div>
          )}
          {deadline && (
            <div className="flex items-center justify-between gap-4">
              <dt className="text-muted-foreground">Confirm by</dt>
              <dd className="text-foreground">{deadline}</dd>
            </div>
          )}
        </dl>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button
          onClick={onConfirm}
          disabled={phase === 'confirming'}
          className="w-full rounded-lg"
        >
          {phase === 'confirming' ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Confirming…
            </>
          ) : (
            <>
              <ShieldCheck className="mr-2 h-4 w-4" />
              {phase === 'failed' ? 'Try again' : 'Confirm payment'}
            </>
          )}
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          You are confirming a payment you already authorised {brand} to take. Your card details are
          held by Stripe. We never see your full card number.
        </p>
      </div>
    </div>
  )
}

// Done — either the customer just cleared the challenge, or it was already resolved before they
// opened the link. Both say the same thing, because both are the same fact.
function ConfirmedCard({
  confirmedNow,
  billingPath,
  signInPath,
}: {
  confirmedNow: boolean
  billingPath: string | null
  signInPath: string
}) {
  return (
    <div className="animate-slide-in overflow-hidden rounded-xl border border-emerald-200 bg-white shadow-sm">
      <div className="bg-gradient-to-b from-emerald-50 to-white px-8 pt-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        </div>
        <h1 className="mt-5 text-xl font-bold tracking-tight text-foreground">
          {confirmedNow ? 'Payment confirmed' : 'This payment is already confirmed'}
        </h1>
        <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
          Thanks, nothing else is needed. Your account updates within a few moments, and your
          receipt lands in your inbox.
        </p>
      </div>

      <div className="space-y-3 p-8 pt-6">
        {billingPath ? (
          <Link
            href={billingPath}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
          >
            <CreditCard className="h-4 w-4" />
            View billing
          </Link>
        ) : (
          <SignInLink signInPath={signInPath} primary />
        )}
      </div>
    </div>
  )
}

// The link is forged, expired, or names a payment that no longer exists — one outcome, so a
// rejected link never confirms which business it belonged to. The way out is the Billing page.
function UnavailableCard({ message, signInPath }: { message: string | null; signInPath: string }) {
  return (
    <div className="animate-slide-in overflow-hidden rounded-xl border border-amber-200 bg-white shadow-sm">
      <div className="bg-gradient-to-b from-amber-50 to-white px-8 pt-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
          <AlertCircle className="h-8 w-8 text-amber-600" />
        </div>
        <h1 className="mt-5 text-xl font-bold tracking-tight text-foreground">
          This link can’t be used
        </h1>
        <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
          {message ??
            'This confirmation link is no longer valid. Sign in and retry the invoice from your Billing page to get a fresh one.'}
        </p>
      </div>

      <div className="space-y-3 p-8 pt-6">
        <SignInLink signInPath={signInPath} primary />
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-border/60 bg-white px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          <LifeBuoy className="h-4 w-4" />
          Contact support
        </a>
      </div>
    </div>
  )
}

function SignInLink({ signInPath, primary }: { signInPath: string; primary?: boolean }) {
  return (
    <Link
      href={signInPath}
      className={
        primary
          ? 'flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90'
          : 'flex w-full items-center justify-center gap-2 rounded-lg border border-border/60 bg-white px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted'
      }
    >
      <LogIn className="h-4 w-4" />
      Sign in to Billing
    </Link>
  )
}
